import { useEffect, useRef } from "react";

const CURSORS = ["🚀", "👾", "🛸", "⚡"];
const ALIEN_EMOJIS = ["👾", "👽", "🤖", "💀", "🦑"];

const AtariBackground = () => {
  const canvasRef = useRef(null);
  const stateRef  = useRef({
    mx: typeof window !== "undefined" ? window.innerWidth / 2 : 400,
    my: typeof window !== "undefined" ? window.innerHeight / 2 : 300,
    score: 0,
    lives: 3,
    t: 0,
    cursorIdx: 0,
    aliens: [],
    bullets: [],
    particles: [],
    stars: [],
    autoFireTimer: 0,
    animId: null,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const st  = stateRef.current;

    // ── resize ──
    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // ── stars ──
    st.stars = Array.from({ length: 120 }, () => ({
      x:      Math.random() * window.innerWidth,
      y:      Math.random() * window.innerHeight,
      s:      Math.random() * 2 + 0.5,
      speed:  Math.random() * 0.4 + 0.1,
      bright: Math.random(),
    }));

    // ── alien factory ──
    const makeAlien = (init = false) => ({
      x:           Math.random() * window.innerWidth,
      y:           init ? Math.random() * window.innerHeight : -40,
      emoji:       ALIEN_EMOJIS[Math.floor(Math.random() * ALIEN_EMOJIS.length)],
      speed:       Math.random() * 0.8 + 0.3,
      dx:          (Math.random() - 0.5) * 0.8,
      size:        Math.random() * 10 + 14,
      dead:        false,
      explodeTimer:0,
    });
    st.aliens = Array.from({ length: 18 }, () => makeAlien(true));

    // ── bullet factory ──
    const spawnBullet = (x, y) => {
      st.bullets.push({ x, y, dead: false, trail: [] });
      for (let i = 0; i < 6; i++)
        st.particles.push(makeParticle(x, y, "#ff0"));
    };

    // ── particle factory ──
    function makeParticle(x, y, color) {
      return {
        x, y,
        vx:      (Math.random() - 0.5) * 4,
        vy:      (Math.random() - 0.5) * 4,
        life:    30 + Math.random() * 20,
        maxLife: 0,
        color,
        size:    Math.random() * 4 + 2,
        _init:   true,
      };
    }

    // ── draw helpers ──
    const drawShip = (x, y, size) => {
      const s = size / 10;
      ctx.fillStyle = "#00ff44";
      ctx.fillRect(x - s * 2, y - s * 4, s * 4, s * 6);
      ctx.fillRect(x - s * 5, y,          s * 4, s * 2);
      ctx.fillRect(x + s,     y,          s * 4, s * 2);
      ctx.fillRect(x - s,     y - s * 6,  s * 2, s * 2);
      ctx.fillStyle = "#00ffff";
      ctx.fillRect(x - s, y - s * 2, s * 2, s * 2);
      ctx.fillStyle = `hsl(${(st.t * 3) % 360},100%,60%)`;
      ctx.fillRect(x - s * 2, y + s * 2, s * 4, s * 2);
    };

    const drawExplosion = (x, y, timer) => {
      const cols = ["#f00","#f80","#ff0","#fff"];
      [6, 12, 18].forEach((r, ri) => {
        ctx.strokeStyle = cols[ri];
        ctx.lineWidth   = 2;
        ctx.beginPath();
        ctx.arc(x, y, r + (15 - timer) * 2, 0, Math.PI * 2);
        ctx.stroke();
      });
    };

    // ── event listeners ──
    const onMouseMove = (e) => { st.mx = e.clientX; st.my = e.clientY; };
    const onTouch     = (e) => {
      st.mx = e.touches[0].clientX;
      st.my = e.touches[0].clientY;
    };
    const onClick = () => {
      st.cursorIdx = (st.cursorIdx + 1) % CURSORS.length;
      spawnBullet(st.mx, st.my);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("touchmove",  onTouch, { passive: true });
    window.addEventListener("click",      onClick);

    // ── main loop ──
    const loop = () => {
      st.t++;
      const W = canvas.width, H = canvas.height;

      // fade trail
      ctx.fillStyle = "rgba(0,0,0,0.22)";
      ctx.fillRect(0, 0, W, H);

      // stars
      st.stars.forEach(s => {
        s.y += s.speed;
        if (s.y > H) { s.y = 0; s.x = Math.random() * W; }
        const a = 0.4 + s.bright * 0.6 * (0.8 + Math.sin(st.t * 0.05 + s.x) * 0.2);
        ctx.fillStyle = `rgba(255,255,255,${a})`;
        ctx.fillRect(Math.round(s.x), Math.round(s.y), Math.ceil(s.s), Math.ceil(s.s));
      });

      // CRT scanlines
      for (let y = 0; y < H; y += 4) {
        ctx.fillStyle = "rgba(0,0,0,0.07)";
        ctx.fillRect(0, y, W, 1);
      }

      // auto-fire
      st.autoFireTimer++;
      if (st.autoFireTimer > 180) {
        st.autoFireTimer = 0;
        spawnBullet(st.mx, st.my);
      }

      // bullets
      for (let i = st.bullets.length - 1; i >= 0; i--) {
        const b = st.bullets[i];
        b.trail.push({ x: b.x, y: b.y });
        if (b.trail.length > 12) b.trail.shift();
        b.y -= 12;

        // hit aliens
        for (let j = st.aliens.length - 1; j >= 0; j--) {
          const a = st.aliens[j];
          if (a.dead) continue;
          if (Math.abs(b.x - a.x) < 20 && Math.abs(b.y - a.y) < 20) {
            a.dead = true; a.explodeTimer = 15;
            b.dead = true;
            st.score += 10;
            for (let k = 0; k < 20; k++)
              st.particles.push(makeParticle(a.x, a.y,
                ["#f00","#f80","#ff0","#0f0","#0ff"][Math.floor(Math.random()*5)]));
            break;
          }
        }

        if (b.y < -20 || b.dead) { st.bullets.splice(i, 1); continue; }

        // draw bullet
        b.trail.forEach((p, ti) => {
          ctx.fillStyle = `rgba(0,255,200,${ti / b.trail.length})`;
          ctx.fillRect(p.x - 2, p.y - 2, 4, 4);
        });
        ctx.fillStyle = "#00ffcc";
        ctx.fillRect(b.x - 3, b.y - 8, 6, 16);
        ctx.fillStyle = "#fff";
        ctx.fillRect(b.x - 1, b.y - 10, 2, 4);
      }

      // aliens
      st.aliens.forEach((a, idx) => {
        a.x += a.dx + Math.sin(st.t * 0.02 + a.y * 0.01) * 0.5;
        a.y += a.speed;

        if (a.dead) {
          a.explodeTimer--;
          drawExplosion(a.x, a.y, a.explodeTimer);
          if (a.explodeTimer <= 0) Object.assign(a, makeAlien(false));
          return;
        }

        if (a.y > H + 40) Object.assign(a, makeAlien(false));

        // player collision
        if (Math.abs(a.x - st.mx) < 25 && Math.abs(a.y - st.my) < 25) {
          a.dead = true; a.explodeTimer = 15;
          st.lives = Math.max(0, st.lives - 1);
          for (let k = 0; k < 15; k++) st.particles.push(makeParticle(st.mx, st.my, "#f00"));
          if (st.lives <= 0) { st.score = 0; st.lives = 3; }
        }

        ctx.globalAlpha = 0.85 + Math.sin(st.t * 0.1 + idx) * 0.15;
        ctx.font = `${a.size}px serif`;
        ctx.textAlign    = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(a.emoji, a.x, a.y);
        ctx.globalAlpha = 1;
      });

      // player ship
      drawShip(st.mx, st.my, 20);

      // particles
      for (let i = st.particles.length - 1; i >= 0; i--) {
        const p = st.particles[i];
        if (p._init) { p.maxLife = p.life; p._init = false; }
        p.x += p.vx; p.y += p.vy; p.vy += 0.1; p.life--;
        if (p.life <= 0) { st.particles.splice(i, 1); continue; }
        ctx.globalAlpha = p.life / p.maxLife;
        ctx.fillStyle   = p.color;
        ctx.fillRect(p.x - p.size/2, p.y - p.size/2, p.size, p.size);
        ctx.globalAlpha = 1;
      }

      // HUD border
      ctx.fillStyle = "#00ff44";
      ctx.fillRect(0, 0, W, 2);
      ctx.fillRect(0, H - 2, W, 2);

      st.animId = requestAnimationFrame(loop);
    };

    st.animId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(st.animId);
      window.removeEventListener("resize",     resize);
      window.removeEventListener("mousemove",  onMouseMove);
      window.removeEventListener("touchmove",  onTouch);
      window.removeEventListener("click",      onClick);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0, left: 0,
        width: "100%", height: "100%",
        zIndex: 0,
        display: "block",
        background: "#000",
        imageRendering: "pixelated",
        pointerEvents: "none",
      }}
    />
  );
};

export default AtariBackground;